import { Helmet } from "react-helmet-async";
import { Link } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertCircle, ArrowRight, Home, ShoppingBag, BookOpen, MessageCircle } from "lucide-react";

const popularDestinations = [
  { href: "/", icon: Home, label: "Home", description: "Start over from the homepage" },
  { href: "/solar-powered-air-conditioners", icon: ShoppingBag, label: "Shop Solar AC Systems", description: "Browse our full product lineup" },
  { href: "/solar-ac-resources", icon: BookOpen, label: "Resources & Guides", description: "Learn about solar cooling" },
  { href: "/solar-ac-support", icon: MessageCircle, label: "Contact Support", description: "Get help from our team" },
];

const popularArticles = [
  { href: "/solar-ac-resources/homeowners-guide-solar-powered-air-conditioning", label: "Homeowner's Guide to Solar AC" },
  { href: "/solar-ac-resources/solar-ac-vs-traditional", label: "Solar AC vs Traditional AC" },
  { href: "/solar-ac-resources/how-much-save-solar-ac", label: "How Much Can You Save?" },
  { href: "/solar-ac-resources/diy-solar-ac-installation", label: "DIY Installation Guide" },
];

export default function NotFound() {
  return (
    <>
      <Helmet>
        <title>Page Not Found | Caribbean Breeze HVAC</title>
        <meta name="description" content="The page you are looking for could not be found. Browse our solar-powered AC systems and expert guides at Caribbean Breeze HVAC." />
        <meta name="robots" content="noindex, follow" />
      </Helmet>
      <div className="min-h-screen w-full py-12 sm:py-16 px-4">
        <div className="max-w-3xl mx-auto">
          <Card className="mb-8">
            <CardContent className="pt-8 pb-8 text-center">
              <div className="flex justify-center mb-4">
                <AlertCircle className="h-12 w-12 text-primary" />
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold mb-3" data-testid="text-404-title">
                Page Not Found
              </h1>
              <p className="text-muted-foreground mb-6 max-w-lg mx-auto">
                The page you're looking for has moved or doesn't exist. Try one of the popular destinations below, or head back to the homepage.
              </p>
              <Link href="/">
                <Button size="lg" data-testid="button-home">
                  <Home className="w-4 h-4 mr-2" />
                  Back to Homepage
                </Button>
              </Link>
            </CardContent>
          </Card>

          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-4">Popular destinations</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {popularDestinations.map((d) => {
                const Icon = d.icon;
                return (
                  <Link
                    key={d.href}
                    href={d.href}
                    className="flex items-start gap-3 p-4 rounded-lg border hover-elevate active-elevate-2 transition-colors"
                    data-testid={`link-popular-${d.href.replace(/\//g, "-")}`}
                  >
                    <Icon className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="font-medium">{d.label}</div>
                      <div className="text-sm text-muted-foreground">{d.description}</div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>

          <div>
            <h2 className="text-xl font-semibold mb-4">Popular guides</h2>
            <div className="space-y-2">
              {popularArticles.map((a) => (
                <Link
                  key={a.href}
                  href={a.href}
                  className="flex items-center justify-between p-3 rounded-lg border hover-elevate active-elevate-2 transition-colors"
                  data-testid={`link-article-${a.href.split("/").pop()}`}
                >
                  <span className="font-medium">{a.label}</span>
                  <ArrowRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
